import type { AuthSupportModeDto, AuthVenueSupportDto } from '@lib/dto/auth.dto'
import type { User } from '@/schemas/auth.schema'

/** Wire user in SUPER_ADMIN support mode (narrowed tenantId + support payload). */
export type SupportModeUser = Extract<
  User,
  { role: 'SUPER_ADMIN'; tenantId: string; support: AuthSupportModeDto }
>

/**
 * True when a SUPER_ADMIN has entered a tenant (support mode). Narrows to the
 * support variant of {@link User} so `tenantId` and `support` are definite.
 */
export function isInTenantSupportMode(user: User | null | undefined): user is SupportModeUser {
  return user != null && user.role === 'SUPER_ADMIN' && user.support != null && user.tenantId != null
}

/** Wire user that has entered a single venue ("log in to venue"). */
export type VenueSupportUser = User & { supportVenue: AuthVenueSupportDto }

/**
 * True when the session is working inside a single venue — a venue admin, or an
 * organization admin or super admin who logged in to one.
 *
 * Do **not** pair this with a role check. It already answers the only question
 * that matters ("am I in a venue"), and adding `worksAtAssignedVenues(user) && …` silently
 * excludes an entered organization admin, who then keeps organization-wide
 * chrome while the rest of their session is scoped to one venue. That mistake has
 * been made three times: the sidebar, the venue breadcrumb, and the events list.
 */
export function isInVenueSupportMode(
  user: User | null | undefined,
): user is VenueSupportUser {
  return user != null && 'supportVenue' in user && user.supportVenue != null
}

/**
 * True for a tenant-org admin, including a SUPER_ADMIN in tenant support mode
 * (they have org-admin powers inside the entered tenant). Only for what is an
 * organization admin's whatever any role allows — roles and billing. Everything
 * else asks `can` (`@/auth/access`), which organization admins always pass.
 */
export function isOrganizationAdmin(user: User | null | undefined): boolean {
  return user?.role === 'ORGANIZATION_ADMIN' || isInTenantSupportMode(user)
}

/** True for the platform super admin identity (whether or not in support mode). */
export function isSuperAdmin(user: User | null | undefined): user is Extract<User, { role: 'SUPER_ADMIN' }> {
  return user?.role === 'SUPER_ADMIN'
}

/** True for a vendor user, who manages a single vendor outside any tenant. */
export function isVendor(user: User | null | undefined): user is Extract<User, { role: 'VENDOR' }> {
  return user?.role === 'VENDOR'
}
